import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import {
  adminGradeWritingSubmission,
  adminListWritingSubmissions,
} from "@/lib/api/writing-academy.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/writing-academy")({
  head: () => ({ meta: [{ title: "Writing Academy review · Admin" }] }),
  component: AdminPage,
});

function AdminPage() {
  const qc = useQueryClient();
  const fetchList = useServerFn(adminListWritingSubmissions);
  const grade = useServerFn(adminGradeWritingSubmission);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-writing-subs"],
    queryFn: () => fetchList(),
  });

  const gradeMut = useMutation({
    mutationFn: (vars: {
      submissionId: string;
      score: number;
      feedback: string;
      decision: "pass" | "revise";
    }) => grade({ data: vars }),
    onSuccess: () => {
      toast.success("Grade saved");
      qc.invalidateQueries({ queryKey: ["admin-writing-subs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save"),
  });

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-5 text-primary" />
        <h1 className="text-2xl font-semibold">Writing Academy — submissions</h1>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Confirm AI grades, override scores, and pass exercises so certificates issue.
      </p>

      {isLoading ? (
        <div className="py-10 text-center">
          <Loader2 className="size-6 animate-spin mx-auto text-muted-foreground" />
        </div>
      ) : !data?.submissions.length ? (
        <p className="mt-8 text-muted-foreground">No submissions yet.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {data.submissions.map((s: any) => (
            <SubmissionRow key={s.id} sub={s} onGrade={(v) => gradeMut.mutate({ submissionId: s.id, ...v })} pending={gradeMut.isPending} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionRow({
  sub,
  onGrade,
  pending,
}: {
  sub: any;
  onGrade: (v: { score: number; feedback: string; decision: "pass" | "revise" }) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState<number>(sub.admin_score ?? sub.ai_score ?? 70);
  const [feedback, setFeedback] = useState<string>(sub.admin_feedback ?? "");
  const lessonTitle = sub.exercise?.lesson?.title ?? "—";
  const courseSlug = sub.exercise?.lesson?.course_slug ?? "—";

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs text-muted-foreground font-mono">{courseSlug}</div>
          <div className="font-semibold">{lessonTitle}</div>
          <div className="text-sm text-muted-foreground mt-1 line-clamp-1">
            {sub.exercise?.prompt}
          </div>
        </div>
        <div className="text-right shrink-0">
          <StatusPill status={sub.status} />
          <div className="text-xs text-muted-foreground mt-1">
            {sub.word_count} words · AI {sub.ai_score ?? "—"}/100
          </div>
        </div>
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-3 text-sm text-primary hover:underline"
      >
        {open ? "Hide" : "Review draft"}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <pre className="rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap font-sans max-h-72 overflow-auto">
            {sub.draft_text}
          </pre>

          {sub.ai_feedback?.summary && (
            <div className="rounded-md border bg-background p-3 text-sm">
              <div className="font-medium">AI summary</div>
              <p className="text-muted-foreground mt-1">{sub.ai_feedback.summary}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-[120px_1fr] gap-3">
            <label className="text-sm">
              Score
              <input
                type="number"
                min={0}
                max={100}
                value={score}
                onChange={(e) => setScore(Number(e.target.value))}
                className="mt-1 w-full rounded-md border bg-background px-2 py-1"
              />
            </label>
            <label className="text-sm">
              Reviewer feedback
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border bg-background px-2 py-1"
              />
            </label>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              disabled={pending || feedback.trim().length < 3}
              onClick={() => onGrade({ score, feedback, decision: "revise" })}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              <XCircle className="size-4" /> Request revision
            </button>
            <button
              disabled={pending || feedback.trim().length < 3}
              onClick={() => onGrade({ score, feedback, decision: "pass" })}
              className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm hover:bg-primary/90"
            >
              <CheckCircle2 className="size-4" /> Pass
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    passed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    revise: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    ai_graded: "bg-primary/10 text-primary",
    submitted: "bg-muted text-foreground",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${map[status] ?? "bg-muted"}`}>
      {status}
    </span>
  );
}
